import type { TWidget } from 'widgets/Widget';
import { TProduct as TCoreProduct } from './CoreProductDetail';
import { TInputRadioInstance } from 'widgets/forms/InputRadio';
import { TAvailabilityModel, TProductRangePrice, TProductDefaultPrice, TGTMInfo } from 'types/models/product';

export type TDeliveryMethodData = {
    isBOPIS?: boolean;
    storeId?: string | undefined;
};

export type TDeliveryBopisData = {
    available: boolean;
    storeName: string;
    storeId: string;
    availableInStore: boolean;
    estimatedPickupDate: string;
    availabilityClass: string;
    pid: string;
    isProductVariant: boolean;
};

const DELIVERY_TYPE_BOPIS = 'bopis';

/**
 * @description ProductDeliveryOptions implementation
 * @param Widget Base widget for extending
 * @returns Product delivery options widget
 */
function ProductDeliveryOptionsClassCreator(Widget: TWidget) {
    class ProductDeliveryOptions extends Widget {
        isBOPISSelected = false;

        selectedStoreID: string | undefined;

        selectedStoreName: string = '';

        productAvailable = false;

        productAvailabilityModel: TAvailabilityModel = {} as TAvailabilityModel;

        productPrice = '0.00';

        productVariantId = '';

        productVariationGroupId = '';

        productGtmInfo: TGTMInfo | undefined;

        bopis: TDeliveryBopisData | null = null;

        isPickupRestricted = false;

        prefs() {
            return {
                switchDeliveryTypeUrl: '',
                uuid: '',
                productAvailability: null,
                productAvailable: false,
                selectedStoreName: '',
                productPrice: '0.00',
                productVariantId: '',
                productVariationGroupId: '',
                isCartPage: false,
                bopis: <TDeliveryBopisData | null>null,
                ...super.prefs()
            };
        }

        init() {
            super.init();
            this.productAvailabilityModel = this.prefs().productAvailability || ({} as TAvailabilityModel);
            this.productAvailable = this.prefs().productAvailable;
            this.selectedStoreName = this.prefs().selectedStoreName;
            this.productPrice = Number(this.prefs().productPrice).toFixed(2).toString();
            this.productVariantId = this.prefs().productVariantId;
            this.productVariationGroupId = this.prefs().productVariationGroupId;
            this.bopis = this.prefs().bopis;
            this.eventBus().on('deliveryoption.updateSelectedBopis', 'updateSelectedBopis');
        }

        /**
         * @description Updates the GTM (Google Tag Manager) information for the product
         * to reflect the current BOPIS (Buy Online, Pickup In Store) selection status.
         */
        setBopisProductGtmInfo() {
            if (this.productGtmInfo) {
                this.productGtmInfo.isBOPISSelected = this.isBOPISSelected;
            } else {
                const pdpMain = document.querySelector('.js-pdp-main');

                if (pdpMain) {
                    const productGtmInfo = JSON.parse(pdpMain.getAttribute('data-analytics') || '{}') as TGTMInfo;

                    productGtmInfo.isBOPISSelected = this.isBOPISSelected;
                    pdpMain.setAttribute('data-analytics', JSON.stringify(productGtmInfo));
                }
            }
        }

        /**
         * @description Update Product Delivery Options
         * @param product productModel
         */
        update(product: TCoreProduct) {
            this.productAvailabilityModel = product.availability;
            this.productAvailable = product.available;

            if (product.gtmInfo && product.bopis.available) {
                this.productGtmInfo = product.gtmInfo;
                this.setBopisProductGtmInfo();
            }

            const rangePrice = product.price as TProductRangePrice;

            if (rangePrice.isRangePrice) {
                this.productPrice = rangePrice.min.sales.decimalPrice;
            } else {
                const defaultPrice = product.price as TProductDefaultPrice;

                this.productPrice = defaultPrice.sales.decimalPrice;
            }

            this.productVariantId = product.bopis.isProductVariant ? product.bopis.pid : '';
            this.productVariationGroupId = product.variationGroupPid;

            return this.updateSelectedBopis(product.bopis);
        }

        /**
         * @description Update Bopis Product Delivery Options
         * @param bopis bopis data from productModel
         */
        updateSelectedBopis(bopis: TDeliveryBopisData | null) {
            if (!bopis) {
                return Promise.resolve();
            }

            this.bopis = bopis;

            if ((!bopis.availableInStore || this.isPickupRestricted) && this.isBOPISSelected) {
                this.isBOPISSelected = false;
            }

            this.selectedStoreID = bopis.storeId;
            this.selectedStoreName = bopis.storeName;
            this.emit('deliverychanged', this.isBOPISSelected, this.selectedStoreID);

            return this.render(
                'template',
                {
                    bopis: bopis,
                    availability: this.productAvailabilityModel,
                    productAvailable: this.productAvailable,
                    isBOPISSelected: this.isBOPISSelected,
                    isPickupRestricted: this.isPickupRestricted
                },
                this.ref('container')
            );
        }

        /**
         * @description Hide or show the pick up in store option, e.g. for the personalized product.
         * @param isRestricted - true to hide the pick up in store option
         * @returns rendering promise
         */
        togglePickupRestriction(isRestricted: boolean) {
            if (this.isPickupRestricted === isRestricted) {
                return Promise.resolve();
            }

            this.isPickupRestricted = isRestricted;

            return this.updateSelectedBopis(this.bopis);
        }

        /**
         * @description Emits GTM events for PDP delivery option changes.
         *
         * - If BOPIS is selected, it emits a `bopis.storePickup.click` event with product and store details.
         * - Otherwise, it emits a `bopis.shipToMe.click` event with product details.
         *
         * These events are used for analytics tracking.
         * @emits bopis.storePickup.click
         * @emits bopis.shipToMe.click
         */
        emitPDPChangeDeliveryEvents() {
            if (this.isBOPISSelected) {
                this.eventBus().emit('bopis.storePickup.click', {
                    price: this.productPrice,
                    variantId: this.productVariantId,
                    variationGroupId: this.productVariationGroupId,
                    store: this.selectedStoreName
                });
            } else {
                this.eventBus().emit('bopis.shipToMe.click', {
                    price: this.productPrice,
                    variantId: this.productVariantId,
                    variationGroupId: this.productVariationGroupId
                });
            }
        }

        /**
         * @description Handler for delivery method change
         * @param selectWidget - Radio input widget that emit event
         */
        selectDeliveryMethod(selectWidget: TInputRadioInstance) {
            this.isBOPISSelected = selectWidget.getDataValue() === DELIVERY_TYPE_BOPIS;
            const uuid = this.prefs().uuid;

            this.emit('deliverychanged', this.isBOPISSelected, this.selectedStoreID);
            this.emit('switchdeliverytype', this.isBOPISSelected, uuid);

            if (this.prefs().isCartPage) {
                if (!this.isBOPISSelected) {
                    this.eventBus().emit('bopis.switchToShipping.click');
                }
            } else {
                this.emitPDPChangeDeliveryEvents();
                this.setBopisProductGtmInfo();
            }
        }
    }

    return ProductDeliveryOptions;
}

export type TProductDeliveryOptions = ReturnType<typeof ProductDeliveryOptionsClassCreator>;

export type TProductDeliveryOptionsInstance = InstanceType<TProductDeliveryOptions>;

export default ProductDeliveryOptionsClassCreator;
